import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import Quill from 'quill';
import { PositionService } from '../../position/services/position.service';
import {
  JobPostService,
  CreateJobPostRequest,
} from '../services/job-post.service';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

interface JobPosition {
  title: string | undefined;
  id: string | number | undefined;
}

interface CareerLevelOption {
  label: string;
  value: string;
}

interface CountryCitiesData {
  countries: string[];
  cities: { [key: string]: string[] };
}

@Component({
  selector: 'app-add-job',
  templateUrl: './add-job.component.html',
  styleUrls: ['./add-job.component.css'],
})
export class AddJobComponent implements OnInit {
  @ViewChild('categoryInputRef') categoryInputRef!: ElementRef;
  @ViewChild('skillInputRef') skillInputRef!: ElementRef;

  form!: FormGroup;
  hideSalary = false;
  vacanciesCount = 1;
  selectedAppMethod = 'platform';
  externalLink = '';
  selectedSkills: string[] = [];
  skillInput = '';
  jobDescription = '';
  requirementsInput = '';
  benefitsInput = '';
  responsibilityInput = '';
  additionalSalaryDetails = '';
  applicationDeadline = '';
  maxApplicants = '';
  questions: { question: string; required: boolean }[] = [
    { question: '', required: true },
  ];
  isConfidential = false;
  isFeatured = false;
  featuredLocked = false;
  confidentialLocked = false;
  featuredExpiresAt = '';
  confidentialExpiresAt = '';
  isPaid = false;
  showSuccessModal = false;
  successIsEdit = false;

  // ── Job status (PUBLISHED / DRAFT) ──────────────────────────
  currentJobStatus: 'PUBLISHED' | 'DRAFT' = 'PUBLISHED';

  selectedCategories: string[] = [];
  categoryInput = '';
  positionInput = '';
  editJobId: string | null = null;
  get isEditMode(): boolean {
    return !!this.editJobId;
  }
  jobId: string = '';

  get userLang(): string {
    return this.translate.currentLang || 'en';
  }

  goBack(): void {
    window.history.back();
  }
  get questionsEnabled(): boolean {
    return this.selectedAppMethod === 'platform';
  }

  get questionsDisabled(): boolean {
    return this.selectedAppMethod === 'external';
  }

  get locationFieldLabel(): string {
    return this.form?.get('country')?.value === 'Egypt'
      ? 'Governorate'
      : 'City';
  }

  // Show "Move to Draft" only when editing an already-published post
  get canMoveToDraft(): boolean {
    return this.isEditMode && this.currentJobStatus === 'PUBLISHED';
  }

  get primaryActionLabelKey(): string {
    if (!this.isEditMode) return 'addJob.publishPost';
    return this.currentJobStatus === 'DRAFT'
      ? 'addJob.publishPost'
      : 'addJob.saveChanges';
  }

  sections: { [key: string]: boolean } = {
    basics: true,
    salary: false,
    about: false,
    appSettings: false,
    visibility: false,
  };

  openDropdowns: { [key: string]: boolean } = {};

  toggleDropdown(dropdownName: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    for (const key in this.openDropdowns) {
      if (key !== dropdownName) {
        this.openDropdowns[key] = false;
      }
    }
    this.openDropdowns[dropdownName] = !this.openDropdowns[dropdownName];
  }

  selectDropdownOption(controlName: string, value: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.form.contains(controlName)) {
      this.form.get(controlName)?.setValue(value);
      this.form.get(controlName)?.markAsTouched();
    }
    this.openDropdowns[controlName] = false;
  }

  selectMethodOption(value: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectAppMethod(value);
    this.openDropdowns['appMethod'] = false;
  }

  @HostListener('document:click', ['$event'])
  closeAllDropdowns(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-select')) {
      for (const key in this.openDropdowns) {
        this.openDropdowns[key] = false;
      }
    }
  }

  getPostTypeLabel(): string {
    const val = this.form.get('postType')?.value;
    const opt = this.data.postTypes.find((p) => p.value === val);
    return opt ? opt.label : 'Select post type';
  }

  getJobPositionLabel(): string {
    return this.positionInput || 'Select position';
  }

  getCareerLevelLabel(): string {
    const val = this.form.get('careerLevel')?.value;
    const opt = this.data.careerLevels.find((level) => level.value === val);
    return opt ? opt.label : 'Select level';
  }

  getAppMethodLabel(): string {
    return this.selectedAppMethod === 'external'
      ? 'External link'
      : 'Upload CV (Platform)';
  }

  private editorsInitialized: { [key: string]: boolean } = {};

  data = {
    postTypes: [
      { label: 'Job', value: 'job' },
      { label: 'Internship', value: 'internship' },
    ],
    jobPositions: [] as JobPosition[],
    careerLevels: [
      { label: 'Manager', value: 'MANAGER' },
      { label: 'Experienced', value: 'EXPERIENCED' },
      { label: 'Senior management', value: 'SENIOR_MANAGEMENT' },
      { label: 'C-Level', value: 'C_LEVEL' },
      { label: 'Entry level', value: 'ENTRY_LEVEL' },
      { label: 'Student', value: 'STUDENT' },
    ] as CareerLevelOption[],
    jobCategories: [
      'Software Development',
      'Design & Creative',
      'Marketing',
      'Sales',
      'Customer Support',
      'Human Resources',
      'Finance & Accounting',
      'Engineering',
      'Healthcare',
      'Education',
    ],
    jobTypes: [
      'Full-Time',
      'Part-Time',
      'Freelance/Project',
      'Shift-based',
      'Volunteering',
    ],
    workplaces: ['Onsite', 'Remote', 'Hybrid'],
    collar: ['Blue Collar', 'White Collar'],
    countries: [] as string[],
    cities: {} as { [key: string]: string[] },
    experienceYears: [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '10+',
    ],
    currencies: ['EGP', 'USD', 'EUR', 'SAR', 'AED', 'GBP'],
    periods: ['Per Month', 'Per Year', 'Per Day', 'Per Hour'],
  };

  selectedJobType = '';
  selectedWorkplace = '';
  availableCities: string[] = [];
  showConfirmModal = false;
  private readonly locationDataUrl = 'assets/data/countries-cities.json';

  featuredPlans = [
    { days: 1, price: 50 },
    { days: 3, price: 100 },
    { days: 7, price: 200 },
  ];
  selectedFeaturedPlan = this.featuredPlans[1];

  confidentialPlans = [
    { days: 1, price: 50 },
    { days: 3, price: 100 },
    { days: 7, price: 200 },
  ];
  selectedConfidentialPlan = this.confidentialPlans[0];

  get modalTotal(): number {
    let total = 0;
    if (this.isFeatured && !this.featuredLocked) {
      total += this.selectedFeaturedPlan?.price ?? 0;
    }
    if (this.isConfidential && !this.confidentialLocked) {
      total += this.selectedConfidentialPlan?.price ?? 0;
    }
    return total;
  }

  get hasAnyPremiumLocked(): boolean {
    return this.featuredLocked || this.confidentialLocked;
  }

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private positionService: PositionService,
    private jobPostService: JobPostService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.watchCountryChanges();
    this.loadCountryCities();
    this.getCompanyJobPosition();

    this.editJobId = this.route.snapshot.paramMap.get('id');
    if (this.editJobId) {
      this.loadJobForEdit(this.editJobId);
    }
  }

  private getCompanyJobPosition(): void {
    const companyId = localStorage.getItem('userId') || '';
    this.positionService.getPositions(0, 100).subscribe({
      next: (res: any) => {
        this.data.jobPositions = res.positions.content.map((p: any) => ({
          title: p.title,
          id: p.id,
        }));
      },
      error: (err) => console.error('Failed to load positions', err),
    });
  }

  private normalizeWorkplace(value: string): string {
    const map: { [key: string]: string } = {
      ONSITE: 'Onsite',
      REMOTE: 'Remote',
      HYBRID: 'Hybrid',
    };
    return map[value?.toUpperCase()] ?? value;
  }

  private normalizeJobType(value: string): string {
    const map: { [key: string]: string } = {
      'FULL-TIME': 'Full-Time',
      'PART-TIME': 'Part-Time',
      'FREELANCE/PROJECT': 'Freelance/Project',
      'SHIFT-BASED': 'Shift-based',
      VOLUNTEERING: 'Volunteering',
    };
    return map[value?.toUpperCase()] ?? value;
  }

  private normalizeCollarType(value: string): string {
    const map: { [key: string]: string } = {
      BLUE_COLLAR: 'Blue Collar',
      WHITE_COLLAR: 'White Collar',
    };
    return map[value?.toUpperCase()] ?? value;
  }

  private normalizeSalaryType(value: string): string {
    const map: { [key: string]: string } = {
      PER_MONTH: 'Per Month',
      PER_YEAR: 'Per Year',
      PER_DAY: 'Per Day',
      PER_HOUR: 'Per Hour',
    };
    return map[value?.toUpperCase()] ?? value;
  }

  // ── Map display labels → API enum values ────────────────────

  private mapPostType(value: string): string {
    return value.toUpperCase();
  }

  private mapWorkplace(value: string): string {
    return value.toUpperCase();
  }

  private mapJobType(value: string): string {
    const map: { [key: string]: string } = {
      'FULL-TIME': 'Full-time',
      'PART-TIME': 'Part-time',
      'FREELANCE/PROJECT': 'Freelance/Project',
      'SHIFT-BASED': 'Shift-based',
      VOLUNTEERING: 'Volunteering',
    };
    return map[value?.toUpperCase()] ?? value;
  }

  private mapCollarType(value: string): string {
    return value.toUpperCase().replace(/\s+/g, '_');
  }

  private mapSalaryType(value: string): string {
    return value.toUpperCase().replace(/\s+/g, '_');
  }

  private mapApplicationMethod(value: string): string {
    return value === 'external' ? 'EXTERNAL_LINK' : 'SINGLE_APPLY';
  }

  private parseExperience(value: string): number | null {
    if (!value || value === 'Min' || value === 'Max') return null;
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  }

  // ── Load job for edit ────────────────────────────────────────

  private loadJobForEdit(id: string): void {
    this.jobPostService.getJobById(id).subscribe({
      next: (job: any) => {
        const country = job.country ?? '';
        this.applyPremiumStateFromJob(job);
        this.currentJobStatus =
          job.jobStatus === 'DRAFT' ? 'DRAFT' : 'PUBLISHED';

        this.form.patchValue({
          jobPostName: job.jobPostTitle ?? job.jobPostName ?? job.title ?? '',
          postType: job.postType?.toLowerCase() ?? 'job',
          jobPosition: job.positionTitle ?? '',
          careerLevel: job.jobLevel ?? '',
          jobTypeDropdown: job.collarType
            ? this.normalizeCollarType(job.collarType)
            : '',
          expMin: job.minExperience ?? 'Min',
          expMax: job.maxExperience ?? 'Max',
          salaryMin: job.minSalary ?? '',
          salaryMax: job.maxSalary ?? '',
          currency: job.currency ?? 'EGP',
          period: job.salaryType
            ? this.normalizeSalaryType(job.salaryType)
            : 'Per Month',
          jobDescription: job.jobDescription ?? '',
        });

        this.form.get('country')?.setValue(country, { emitEvent: false });
        this.availableCities = this.data.cities[country] || [];
        this.form.get('city')?.setValue(job.city ?? '', { emitEvent: false });

        this.selectedJobType = job.jobType
          ? this.normalizeJobType(job.jobType)
          : '';
        this.selectedWorkplace = job.workplace
          ? this.normalizeWorkplace(job.workplace)
          : '';
        this.positionInput = job.positionTitle ?? '';

        this.hideSalary = !!job.hideSalary;
        this.jobDescription = job.jobDescription ?? '';
        this.vacanciesCount = job.numberOfVacancies ?? 1;
        this.maxApplicants =
          job.maxApplicants != null ? String(job.maxApplicants) : '';
        this.selectedSkills = job.skillsAndTools ?? [];
        this.additionalSalaryDetails = job.additionalSalaryDetails ?? '';
        this.selectedCategories = Array.isArray(job.categories)
          ? job.categories.map((c: any) => c.name ?? c)
          : [];

        this.requirementsInput = Array.isArray(job.requirements)
          ? job.requirements.join('\n')
          : (job.requirements ?? '');
        this.benefitsInput = Array.isArray(job.benefits)
          ? job.benefits.join('\n')
          : (job.benefits ?? '');
        this.responsibilityInput = Array.isArray(job.responsibilities)
          ? job.responsibilities.join('\n')
          : (job.responsibilities ?? '');

        this.applicationDeadline = job.deadline ?? '';

        if (Array.isArray(job.questions) && job.questions.length) {
          this.questions = job.questions
            .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
            .map((q: any) => ({
              question: q.question ?? '',
              required: q.required ?? true,
            }));
        }

        if (job.applicationMethod === 'EXTERNAL_LINK') {
          this.selectedAppMethod = 'external';
          this.externalLink = job.externalLink ?? '';
        } else {
          this.selectedAppMethod = 'platform';
        }

        this.sections['basics'] = true;
      },
      error: (err: any) => {
        console.error('Failed to load job for editing:', err);
        this.showBackendError(err);
      },
    });
  }

  private isFutureDate(dateValue?: string | null): boolean {
    if (!dateValue) return false;
    const expiresAt = new Date(dateValue).getTime();
    return Number.isFinite(expiresAt) && expiresAt > Date.now();
  }

  private getPlanByAmount(
    plans: { days: number; price: number }[],
    amount?: number | null,
  ): { days: number; price: number } | null {
    if (amount == null) return null;
    return plans.find((plan) => plan.price === Number(amount)) ?? null;
  }

  private getPaymentAmount(
    payments: { amount: number; title: string }[] | null | undefined,
    title: string,
  ): number | null {
    if (!Array.isArray(payments)) return null;
    const payment = payments.find(
      (item) => (item.title ?? '').toUpperCase() === title,
    );
    return payment ? Number(payment.amount) : null;
  }

  private applyPremiumStateFromJob(job: any): void {
    const featuredActive =
      !!job.featured && this.isFutureDate(job.featuredExpiresAt);
    const confidentialActive =
      !!job.confidential && this.isFutureDate(job.confidentialExpiresAt);

    this.isFeatured = featuredActive;
    this.isConfidential = confidentialActive;
    this.featuredLocked = featuredActive;
    this.confidentialLocked = confidentialActive;
    this.featuredExpiresAt = job.featuredExpiresAt ?? '';
    this.confidentialExpiresAt = job.confidentialExpiresAt ?? '';

    const featuredPaymentAmount = this.getPaymentAmount(
      job.payments,
      'FEATURED',
    );
    const confidentialPaymentAmount = this.getPaymentAmount(
      job.payments,
      'CONFIDENTIAL',
    );

    const featuredPlan = this.getPlanByAmount(
      this.featuredPlans,
      featuredPaymentAmount,
    );
    const confidentialPlan = this.getPlanByAmount(
      this.confidentialPlans,
      confidentialPaymentAmount,
    );

    if (featuredPlan) this.selectedFeaturedPlan = featuredPlan;
    if (confidentialPlan) this.selectedConfidentialPlan = confidentialPlan;
  }

  getExpiryLabel(dateValue: string): string {
    if (!dateValue) return '';
    return new Date(dateValue).toLocaleDateString();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      jobPostName: ['', Validators.required],
      postType: ['job', Validators.required],
      jobPosition: ['', Validators.required],
      careerLevel: ['', Validators.required],
      jobTypeSelect: ['', Validators.required],
      country: [''],
      city: [''],
      jobTypeDropdown: [''],
      expMin: ['Min', Validators.pattern('^[0-9]+$')],
      expMax: ['Max', Validators.pattern('^[0-9]+$')],
      salaryMin: ['', Validators.pattern('^[0-9]+$')],
      salaryMax: ['', Validators.pattern('^[0-9]+$')],
      currency: ['EGP'],
      period: ['Per Month'],
      jobDescription: ['', Validators.required],
    });
  }

  private readonly swalConfig = {
    customClass: {
      popup: 'swal-popup',
      title: 'swal-title',
      htmlContainer: 'swal-message',
      confirmButton: 'swal-confirm-btn',
      cancelButton: 'swal-cancel-btn',
    },
    buttonsStyling: false,
    reverseButtons: true,
    iconColor: '#D0385E',
    background: '#ffffff',
    color: '#1a1d23',
    didOpen: () => {
      const icon = document.querySelector(
        '.swal2-icon.swal2-success',
      ) as HTMLElement;
      if (icon) icon.style.borderColor = '#F98650';
      const ring = document.querySelector('.swal2-success-ring') as HTMLElement;
      if (ring) ring.style.borderColor = 'rgba(249,134,80,0.25)';
      document
        .querySelectorAll<HTMLElement>('[class^="swal2-success-line"]')
        .forEach((el) => {
          el.style.backgroundColor = '#D0385E';
        });
    },
  };

  // ── Backend error popup ───────────────────────────────────────
  private showBackendError(err: any): void {
    const backendMessage = err?.error?.message || err?.error?.error || null;

    Swal.fire({
      ...this.swalConfig,
      icon: 'error',
      title: this.translate.instant('addJob.somethingWentWrongTitle'),
      text:
        backendMessage || this.translate.instant('addJob.somethingWentWrong'),
      confirmButtonText: this.translate.instant('addJob.ok'),
    });
  }

  private watchCountryChanges(): void {
    this.form.get('country')?.valueChanges.subscribe((country) => {
      this.availableCities = this.data.cities[country] || [];
      this.form.get('city')?.setValue('');
    });
  }

  private loadCountryCities(): void {
    this.http.get<CountryCitiesData>(this.locationDataUrl).subscribe({
      next: (data) => {
        this.data.countries = data.countries ?? [];
        this.data.cities = data.cities ?? {};

        const currentCountry = this.form.get('country')?.value;
        if (currentCountry) {
          this.availableCities = this.data.cities[currentCountry] || [];
        }
      },
      error: (err) => {
        console.error('Failed to load country/city data:', err);
      },
    });
  }

  // ── Quill editors ────────────────────────────────────────────

  private initJobDescriptionEditor(): void {
    const el = document.getElementById('jobDescEditor');
    if (!el) return;
    const quill = new Quill(el, {
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['link', 'blockquote', 'code-block'],
          [
            { align: '' },
            { align: 'center' },
            { align: 'right' },
            { align: 'justify' },
          ],
          [{ direction: 'rtl' }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
        ],
      },
      placeholder: 'Enter your text here',
      theme: 'snow',
    });
    if (this.jobDescription) {
      quill.root.innerHTML = this.jobDescription;
    }
    quill.on('text-change', () => {
      const html: any = quill.root.innerHTML;
      this.jobDescription = html;
      this.form.get('jobDescription')?.setValue(html);
    });
  }

  private initRequirementsEditor(): void {
    const el = document.getElementById('requirementsEditor');
    if (!el) return;
    const quill = new Quill(el, {
      modules: { toolbar: false },
      placeholder: 'Enter requirements...',
      theme: 'snow',
    });
    if (this.requirementsInput) {
      quill.root.innerHTML = this.requirementsInput;
    } else {
      quill.clipboard.dangerouslyPasteHTML('<ul><li><br></li></ul>');
    }
    quill.on('text-change', () => {
      this.requirementsInput = quill.root.innerHTML;
    });
  }

  private initResponsibilitiesEditor(): void {
    const el = document.getElementById('responsibilitiesEditor');
    if (!el) return;
    const quill = new Quill(el, {
      modules: { toolbar: false },
      placeholder: 'Enter Responsibilities...',
      theme: 'snow',
    });
    if (this.responsibilityInput) {
      quill.root.innerHTML = this.responsibilityInput;
    } else {
      quill.clipboard.dangerouslyPasteHTML('<ul><li><br></li></ul>');
    }
    quill.on('text-change', () => {
      this.responsibilityInput = quill.root.innerHTML;
    });
  }

  private initBenefitsEditor(): void {
    const el = document.getElementById('benefitsEditor');
    if (!el) return;
    const quill = new Quill(el, {
      modules: { toolbar: false },
      placeholder: 'Enter benefits...',
      theme: 'snow',
    });
    if (this.benefitsInput) {
      quill.root.innerHTML = this.benefitsInput;
    } else {
      quill.clipboard.dangerouslyPasteHTML('<ul><li><br></li></ul>');
    }
    quill.on('text-change', () => {
      this.benefitsInput = quill.root.innerHTML;
    });
  }

  toggleSection(key: string): void {
    this.sections[key] = !this.sections[key];
    if (
      key === 'about' &&
      this.sections['about'] &&
      !this.editorsInitialized['about']
    ) {
      this.editorsInitialized['about'] = true;
      setTimeout(() => {
        this.initJobDescriptionEditor();
        this.initRequirementsEditor();
        this.initBenefitsEditor();
        this.initResponsibilitiesEditor();
      }, 0);
    }
  }

  // ── Chip toggles ─────────────────────────────────────────────

  toggleJobType(type: string): void {
    this.selectedJobType = this.selectedJobType === type ? '' : type;
  }

  isJobTypeSelected(type: string): boolean {
    return this.selectedJobType === type;
  }

  toggleWorkplace(place: string): void {
    this.selectedWorkplace = this.selectedWorkplace === place ? '' : place;
  }

  isWorkplaceSelected(place: string): boolean {
    return this.selectedWorkplace === place;
  }

  // ── Category tags ─────────────────────────────────────────────

  addCategory(): void {
    const cat = this.categoryInput.trim().replace(/,$/, '');
    if (cat && !this.selectedCategories.includes(cat)) {
      this.selectedCategories.push(cat);
    }
    this.categoryInput = '';
  }

  removeCategory(cat: string): void {
    this.selectedCategories = this.selectedCategories.filter((c) => c !== cat);
  }

  focusCategoryInput(): void {
    this.categoryInputRef?.nativeElement?.focus();
  }

  // ── Vacancies ─────────────────────────────────────────────────

  incrementVacancies(): void {
    this.vacanciesCount++;
  }
  decrementVacancies(): void {
    if (this.vacanciesCount > 1) this.vacanciesCount--;
  }

  // ── Skills ────────────────────────────────────────────────────

  addSkill(): void {
    const rawSkills = this.skillInput
      .split(',')
      .map((skill) => skill.trim())
      .filter((skill) => skill.length > 0);

    rawSkills.forEach((skill) => {
      if (!this.selectedSkills.includes(skill)) {
        this.selectedSkills.push(skill);
      }
    });

    this.skillInput = '';
  }

  removeSkill(skill: string): void {
    this.selectedSkills = this.selectedSkills.filter((s) => s !== skill);
  }

  focusSkillInput(): void {
    this.skillInputRef?.nativeElement?.focus();
  }

  // ── Application method ────────────────────────────────────────

  selectAppMethod(method: string): void {
    this.selectedAppMethod = method;
    if (method === 'platform' && this.questions.length === 0) {
      this.questions = [{ question: '', required: true }];
    }
  }

  addQuestion(): void {
    if (this.questionsDisabled) return;
    this.questions.push({ question: '', required: true });
  }
  removeQuestion(i: number): void {
    if (this.questionsDisabled) return;
    this.questions.splice(i, 1);
  }
  trackByIndex(i: number): number {
    return i;
  }

  // ── Visibility toggles ────────────────────────────────────────

  toggleFeatured(): void {
    if (this.hasAnyPremiumLocked) return;
    this.isFeatured = !this.isFeatured;
  }

  toggleConfidential(): void {
    if (this.hasAnyPremiumLocked) return;
    this.isConfidential = !this.isConfidential;
  }

  selectFeaturedPlan(plan: { days: number; price: number }): void {
    if (this.hasAnyPremiumLocked) return;
    this.selectedFeaturedPlan = plan;
  }

  selectConfidentialPlan(plan: { days: number; price: number }): void {
    if (this.hasAnyPremiumLocked) return;
    this.selectedConfidentialPlan = plan;
  }

  private getFeaturedProductKey(days: number): string {
    return `featured-${days}`;
  }

  private getConfidentialProductKey(days: number): string {
    return `confidential-${days}`;
  }

  private getPromotionProductKey(): string {
    if (this.hasAnyPremiumLocked) return '';
    const keys: string[] = [];

    if (this.isFeatured && !this.featuredLocked) {
      keys.push(this.getFeaturedProductKey(this.selectedFeaturedPlan.days));
    }

    if (this.isConfidential && !this.confidentialLocked) {
      keys.push(
        this.getConfidentialProductKey(this.selectedConfidentialPlan.days),
      );
    }

    return keys.join(',');
  }

  private splitEditorLines(value: string): string[] {
    if (!value) return [];
    const htmlLike = value.replace(/<li><br><\/li>/gi, '').trim();
    if (!htmlLike) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlLike, 'text/html');
    const items = Array.from(doc.querySelectorAll('li'))
      .map((li) => (li.textContent ?? '').trim())
      .filter((item) => item.length > 0);

    if (items.length > 0) return items;

    const text = (doc.body.textContent ?? value)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    return text.length > 0 ? text : [value];
  }

  // ── Confirm modal ─────────────────────────────────────────────

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  confirmPromotion(): void {
    this.showConfirmModal = false;

    const key = this.getPromotionProductKey();
    if (!key) {
      this.router.navigate(['/company-admin/job-posts']);
      return;
    }

    this.makeOrder(this.jobId, key, this.modalTotal);
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.router.navigate(['/company-admin/positions']);
  }

  // ── Submit ────────────────────────────────────────────────────
  // `status` lets the caller decide whether this save should
  // publish the post or move it back to draft.
  submitForm(status: 'PUBLISHED' | 'DRAFT' = 'PUBLISHED'): void {
    if (!this.selectedWorkplace || !this.selectedJobType) {
      return;
    }

    // ── Snapshot premium flags BEFORE the async API call ──────
    const hasPremium = !!this.getPromotionProductKey();

    const fv = this.form.value;

    const filteredQuestions = this.questions
      .map((q, index) => ({
        question: q.question.trim(),
        required: q.required ?? true,
        displayOrder: index,
      }))
      .filter((q) => q.question.length > 0);

    const maxApplicantsValue = this.maxApplicants.trim()
      ? Number(this.maxApplicants)
      : null;

    const payload: CreateJobPostRequest = {
      jobPostTitle: fv.jobPostName,
      positionTitle: fv.jobPosition,
      jobLevel: fv.careerLevel,
      postType: this.mapPostType(fv.postType),
      jobType: this.mapJobType(this.selectedJobType),
      jobStatus: status,
      collarType: fv.jobTypeDropdown
        ? this.mapCollarType(fv.jobTypeDropdown)
        : null,
      categories: this.selectedCategories.length
        ? this.selectedCategories
        : null,
      workplace: this.mapWorkplace(this.selectedWorkplace),
      country: fv.country,
      city: fv.city,
      minExperience: this.parseExperience(fv.expMin),
      maxExperience: this.parseExperience(fv.expMax),
      minSalary: fv.salaryMin ? Number(fv.salaryMin) : null,
      maxSalary: fv.salaryMax ? Number(fv.salaryMax) : null,
      currency: fv.currency ?? null,
      salaryType: fv.period ? this.mapSalaryType(fv.period) : null,
      hideSalary: this.hideSalary,
      additionalSalaryDetails: this.additionalSalaryDetails || null,
      numberOfVacancies: this.vacanciesCount,
      maxApplicants: maxApplicantsValue,
      deadline: this.applicationDeadline,
      jobDescription: this.jobDescription,
      skillsAndTools: this.selectedSkills,
      applicationMethod: this.mapApplicationMethod(this.selectedAppMethod),
      externalLink:
        this.selectedAppMethod === 'external' ? this.externalLink : null,
      questions:
        this.questionsDisabled || filteredQuestions.length === 0
          ? null
          : filteredQuestions,
      requirements: this.splitEditorLines(this.requirementsInput),
      benefits: this.splitEditorLines(this.benefitsInput),
      responsibilities: this.splitEditorLines(this.responsibilityInput),
    };

    if (this.isEditMode) {
      this.jobPostService.updateJobPost(this.editJobId!, payload).subscribe({
        next: (res: any) => {
          this.jobId = res.id;
          this.currentJobStatus = status;

          if (hasPremium) {
            // User selected a premium plan → show confirmation modal only
            this.showConfirmModal = true;
          } else if (status === 'DRAFT') {
            Swal.fire({
              ...this.swalConfig,
              icon: 'success',
              title: this.translate.instant('addJob.jobMovedToDraftTitle'),
              text: this.translate.instant('addJob.jobMovedToDraftText'),
              confirmButtonText: this.translate.instant('addJob.great'),
            }).then(() => {
              this.router.navigate(['/company-admin/job-posts']);
            });
          } else {
            // No plan → show success popup then navigate
            Swal.fire({
              ...this.swalConfig,
              icon: 'success',
              title: this.translate.instant('addJob.jobUpdatedTitle'),
              text: this.translate.instant('addJob.jobUpdatedText'),
              confirmButtonText: this.translate.instant('addJob.great'),
            }).then(() => {
              this.router.navigate(['/company-admin/job-posts']);
            });
          }
        },
        error: (err: any) => {
          console.error('Failed to update job post:', err);
          this.showBackendError(err);
        },
      });
    } else {
      this.jobPostService.createJobPost(payload).subscribe({
        next: (res: any) => {
          this.jobId = res.id;
          this.currentJobStatus = status;

          if (hasPremium) {
            // User selected a premium plan → show confirmation modal only
            this.showConfirmModal = true;
          } else {
            // No plan → show success popup then navigate
            Swal.fire({
              ...this.swalConfig,
              icon: 'success',
              title: this.translate.instant('addJob.jobPublishedTitle'),
              text: this.translate.instant('addJob.jobPublishedText'),
              confirmButtonText: this.translate.instant('addJob.great'),
            }).then(() => {
              this.router.navigate(['/company-admin/job-posts']);
            });
          }
        },
        error: (err: any) => {
          console.error('Failed to create job post:', err);
          this.showBackendError(err);
        },
      });
    }
  }

  makeOrder(jobId: string, type: string, amount?: number): void {
    this.router.navigate(['/payment'], {
      state: {
        productKey: type,
        serviceSystemId: jobId,
        amount: amount ?? this.modalTotal,
      },
      queryParams: {
        productKey: type,
        serviceSystemId: jobId,
        amount: amount ?? this.modalTotal,
      },
    });
  }
}
